import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Check, X, Trash2, LogOut } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import SEO from "@/components/seo/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TABS = ["pending", "approved", "rejected"];

const useReloader = (fn, deps) => {
  useEffect(() => { fn(); }, deps);
};

const AdminGallery = () => {
  const { admin, logout, getToken } = useAuth();
  const [tab, setTab] = useState("pending");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refetch, setRefetch] = useState(0);

  const load = useCallback(async (t) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/gallery?status=${t}`, { headers: { Authorization: `Bearer ${getToken()}` } });
      setItems(data || []);
    } catch (e) { toast.error("Could not load."); }
    finally { setLoading(false); }
  }, [getToken]);

  useReloader(() => load(tab), [tab, load, refetch]);

  const refresh = () => setRefetch((n) => n + 1);

  const setStatus = async (id, status) => {
    try { await axios.patch(`${API}/admin/gallery/${id}?status=${status}`, {}, { headers: { Authorization: `Bearer ${getToken()}` } }); toast.success(status); refresh(); }
    catch { toast.error("Failed"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this entry?")) return;
    try { await axios.delete(`${API}/admin/gallery/${id}`, { headers: { Authorization: `Bearer ${getToken()}` } }); refresh(); }
    catch { toast.error("Failed"); }
  };

  return (
    <main data-testid="admin-gallery-page" className="min-h-screen bg-[#F7F7F4]">
      <SEO title="Gallery Moderation" path="/admin/gallery" noindex />
      <header className="sticky top-0 z-30 bg-[#F7F7F4] border-b border-[#E7E7E2]">
        <div className="max-w-6xl mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
          <Link to="/admin" className="font-sora text-[22px] font-extrabold tracking-tight text-[#111111]">Vaymilo<span className="text-[#2563EB]">.</span></Link>
          <div className="flex items-center gap-3">
            <span className="hidden md:inline font-manrope text-sm text-[#111111]/65">{admin?.email}</span>
            <button onClick={logout} className="inline-flex items-center gap-2 bg-white text-[#111111] font-manrope text-sm px-4 py-2 rounded-lg border border-[#E7E7E2] hover:border-[#111111]">
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 md:px-10 py-12">
        <div className="overline">— Gallery</div>
        <h1 className="mt-3 font-sora text-[40px] md:text-[52px] font-extrabold tracking-tight text-[#111111]">Moderation queue.</h1>
        <Link to="/admin" className="mt-4 inline-block font-manrope text-sm text-[#111111]/65 hover:text-[#111111]">← Back to dashboard</Link>

        <div className="mt-8 flex gap-2">
          {TABS.map((t) => (
            <button key={t} data-testid={`gallery-tab-${t}`} onClick={() => setTab(t)}
              className={`px-4 py-2 rounded-full font-manrope text-sm border transition-colors capitalize ${
                t === tab ? "bg-[#111111] text-white border-[#111111]" : "bg-white text-[#111111]/75 border-[#E7E7E2] hover:border-[#111111]"
              }`}>{t}</button>
          ))}
        </div>

        <section className="mt-8">
          {loading && (
            <div className="font-mono text-sm text-[#111111]/50 tracking-widest uppercase">Loading…</div>
          )}
          {!loading && items.length === 0 && (
            <div className="font-manrope text-[#111111]/65 py-12">Nothing in {tab}.</div>
          )}
          {!loading && items.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#E7E7E2] border border-[#E7E7E2] rounded-2xl overflow-hidden">
              {items.map((it) => (
                <div key={it.id} className="bg-white p-5 flex flex-col">
                  <img src={it.image_url} alt={it.caption} loading="lazy" className="w-full aspect-[4/3] object-cover rounded-lg bg-[#F7F7F4]" />
                  <p className="mt-3 font-manrope text-[14px] text-[#111111]">{it.caption}</p>
                  <p className="mt-1 text-[12px] text-[#111111]/55">— {it.name} · {it.email} · {it.product_slug || "—"}</p>
                  <div className="mt-auto pt-4 flex items-center gap-2">
                    {tab !== "approved" && <button onClick={() => setStatus(it.id, "approved")} className="inline-flex items-center gap-1 bg-[#2563EB] text-white text-[12px] px-3 py-1.5 rounded-md"><Check size={12} /> Approve</button>}
                    {tab !== "rejected" && <button onClick={() => setStatus(it.id, "rejected")} className="inline-flex items-center gap-1 bg-white text-[#111111] border border-[#E7E7E2] text-[12px] px-3 py-1.5 rounded-md"><X size={12} /> Reject</button>}
                    <button onClick={() => remove(it.id)} className="ml-auto text-[#111111]/45 hover:text-[#2563EB]"><Trash2 size={14} /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </main>
  );
};

export default AdminGallery;
